import React, { useMemo, useRef } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Box, Typography } from '@mui/material';
import { IllioSearchOption } from '../../types/generatedTypes';
import { IllioSearchCell } from '../../components/Infra/CustomAutocomplete/IllioSearchCell';

interface UseHoldingsColumnsProps {
  autosizeColumns: boolean;
  isGroupingEnabled?: boolean;
  // Illio search props
  searchLoadingRows: Record<string, boolean>;
  handleIllioSearch: (rowId: string, query: string) => void;
  getOptionsForRow: (rowId: string) => IllioSearchOption[];
  handleIllioInstrumentSelect: (
    holdingId: string,
    selectedInstrument: IllioSearchOption | null
  ) => void;
}

export const useHoldingsColumns = ({
  autosizeColumns,
  isGroupingEnabled = false,
  searchLoadingRows,
  handleIllioSearch,
  getOptionsForRow,
  handleIllioInstrumentSelect,
}: UseHoldingsColumnsProps) => {
  // Use refs to access latest values without causing column recreation
  const getOptionsForRowRef = useRef(getOptionsForRow);
  const searchLoadingRowsRef = useRef(searchLoadingRows);

  // Update refs on each render so renderCell always gets current values
  getOptionsForRowRef.current = getOptionsForRow;
  searchLoadingRowsRef.current = searchLoadingRows;

  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      // Dedicated group column - only shown when grouping is enabled
      ...(isGroupingEnabled
        ? [
            {
              field: 'group_name',
              headerName: 'Group',
              ...(autosizeColumns ? {} : { flex: 1.2 }),
              sortable: false,
              filterable: false,
              pinnable: false, // Prevent user from unpinning
              hideable: false, // Prevent user from hiding
              valueGetter: (value: any, row: any) => {
                // For group rows, show the group name
                if (row.isGroup) {
                  return row.groupName;
                }
                // For regular rows, return empty string
                return '';
              },
            },
          ]
        : []),
      {
        field: 'instrument_type',
        headerName: 'Type',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        minWidth: 120,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => params.value,
      },
      {
        field: 'label',
        headerName: 'Illio Label',
        ...(autosizeColumns ? {} : { flex: 2 }),
        minWidth: 300,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          const options = getOptionsForRowRef.current(
            params.row.id,
            params.row.label
          );
          const selectedOption =
            options.find(opt => opt.label === params.row.label) || null;
          const isLoading =
            searchLoadingRowsRef.current[params.row.id] || false;

          return (
            <IllioSearchCell
              rowId={params.row.id}
              selectedOption={selectedOption}
              options={options}
              loading={isLoading}
              onSearch={handleIllioSearch}
              onChange={handleIllioInstrumentSelect}
              textFieldProps={{ variant: 'standard', sx: { width: '100%' } }}
            />
          );
        },
      },
      {
        field: 'isin',
        headerName: 'ISIN',
        ...(autosizeColumns ? {} : { flex: 1 }),
        minWidth: 140,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'symbol',
        headerName: 'Symbol',
        ...(autosizeColumns ? {} : { flex: 1 }),
        minWidth: 120,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'currency',
        headerName: 'Currency',
        ...(autosizeColumns ? {} : { flex: 0.7 }),
        minWidth: 100,
        editable: false,
        sortable: true,
        filterable: true,
      },
      {
        field: 'multiplier',
        headerName: 'Multiplier',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        minWidth: 120,
        editable: true,
        sortable: true,
        filterable: true,
        type: 'number',
        valueSetter: (value, row) => {
          if (value === 0) {
            return row;
          }
          return { ...row, multiplier: value };
        },
        renderCell: params => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {params.row.original_multiplier != null &&
              params.row.multiplier !== params.row.original_multiplier && (
                <Typography
                  component="span"
                  sx={{
                    color: 'warning.main',
                    fontWeight: 'bold',
                    fontSize: 16,
                  }}
                  title="Manually modified"
                >
                  *
                </Typography>
              )}
            {params.value}
          </Box>
        ),
      },
      {
        field: 'effective_multiplier',
        headerName: 'Effective Multiplier',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        minWidth: 140,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number',
        renderCell: params => (
          <Typography sx={{ fontSize: '0.875rem' }}>
            {params.value ?? 'N/A'}
          </Typography>
        ),
      },
    ];

    return cols;
    // Note: searchLoadingRows and getOptionsForRow accessed via refs (not deps)
    // This prevents column recreation on search while still providing current values
  }, [
    autosizeColumns,
    isGroupingEnabled,
    handleIllioSearch,
    handleIllioInstrumentSelect,
  ]);

  return {
    columns,
  };
};
